/**
 * A capa personalizada: escolher, enquadrar e entregar a fotografia.
 *
 * O cliente carrega uma foto, arrasta-a e aproxima-a dentro de uma capa
 * genérica. A foto cobre sempre a capa inteira — o zoom mínimo é o que a
 * cobre, e não se arrasta para lá da borda —, porque um canto vazio no ecrã
 * é um canto por imprimir na capa.
 *
 * Ao concluir, entram no formulário do produto, como ficheiros: a foto na
 * capa, como o cliente a deixou (é a que ele vê no carrinho e a loja vê na
 * encomenda); as costas da capa ao tamanho de impressão; e o original. Vai
 * também a posição em texto, para se poder refazer o enquadramento.
 *
 * Sem fotografia, o formulário não avança: o pedido chegava à loja sem nada
 * para imprimir.
 *
 * Há duas capas: a do 17 Pro / Pro Max (e seguintes), com a câmara numa barra
 * de lado a lado, e a de todos os outros, com o módulo ao canto. A capa segue
 * o iPhone escolhido na página; trocar de iPhone depois de concluir refaz a
 * imagem para impressão na forma da capa nova.
 *
 * Fora da página de produto (o kit NEXO × Vagar), o iPhone e a cor vêm de
 * uma lista de variantes indicada em `data-fonte`, a janela abre-se com
 * `abre()` e, ao concluir, o editor avisa com `claro-foto:concluida` — com os
 * ficheiros — para quem o usa os enviar.
 */

/** Largura da imagem final, em píxeis. A altura segue a proporção da capa. */
const LARGURA_FINAL = 1240;
/**
 * Tudo segue em JPG. O original vai até este lado maior — chega para
 * reimprimir — e, se o envio todo passasse do limite, encolhia. A Shopify
 * recusa pedidos acima de uns 20 MB (testado: 18 MB passou, 26 MB não), e
 * uma encomenda recusada é uma venda perdida.
 */
const LADO_ORIGINAL = 3000;
const LIMITE_ENVIO = 16e6;
/** Abaixo disto, na largura da capa, a foto fica desfocada impressa. */
const MINIMO_NITIDO = 600;
const ZOOM_MAXIMO = 4;
/** Altura ÷ largura das costas impressas de cada capa (as mesmas do CSS) */
const PROPORCAO = { base: 2.0422, '17pro': 2.0131 };
/**
 * O link de uma foto guardada, a entregar sempre o JPG original. Sem isto o
 * navegador recebe WebP. O que não é foto guardada passa como está.
 * @param {unknown} valor
 */
function comoJpg(valor) {
  if (typeof valor !== 'string' || !valor.includes('/uploads/') || valor.includes('format=')) return valor;
  return `${valor}${valor.includes('?') ? '&' : '?'}format=pjpg`;
}

/** Os iPhone com a câmara em barra: 17 Pro e Pro Max, e os Pro que vierem */
const COM_BARRA = /\b1[7-9]\s*pro\b/i;
/**
 * Onde ficam as costas dentro de cada moldura, em frações dela — as mesmas
 * medidas do CSS. Servem para montar a imagem da foto na capa.
 */
const MOLDURA = {
  base: { x: 0.04988, y: 0.01868, l: 0.90024, a: 0.96389, rx: 0.18997, ry: 0.09302 },
  '17pro': { x: 0.05361, y: 0.01877, l: 0.89044, a: 0.96245, rx: 0.18325, ry: 0.09103 },
};

class ClaroFoto extends HTMLElement {
  /** Posição do centro da foto em relação ao centro da capa, em fração da capa */
  #x = 0;
  #y = 0;
  #zoom = 1;
  /** @type {HTMLImageElement | null} */
  #imagem = null;
  /** Dedos ou rato em cima da capa, pelo id do ponteiro */
  #ponteiros = new Map();
  #distancia = 0;
  /** O URL da miniatura em uso, para o largar quando houver outra */
  #urlMini = '';
  /** A capa em uso: 'base' ou '17pro' */
  #modelo = 'base';
  /** Já se concluiu uma vez: a imagem para impressão está no formulário */
  #feito = false;

  connectedCallback() {
    this.caixa = this.querySelector('[data-claro-foto-caixa]');
    this.tel = this.querySelector('[data-claro-foto-tel]');
    this.ecra = this.querySelector('[data-claro-foto-ecra]');
    this.img = this.querySelector('[data-claro-foto-img]');
    this.vazio = this.querySelector('[data-claro-foto-vazio]');
    this.ficheiro = this.querySelector('[data-claro-foto-ficheiro]');
    this.zoom = this.querySelector('[data-claro-foto-zoom]');
    this.concluir = this.querySelector('[data-claro-foto-concluir]');
    this.baixa = this.querySelector('[data-claro-foto-baixa]');
    this.erro = this.querySelector('[data-claro-foto-erro]');
    this.form = this.closest('form');

    if (!(this.caixa instanceof HTMLDialogElement) || !this.tel || !this.ecra || !this.img) return;

    this.controlo = new AbortController();
    const { signal } = this.controlo;

    for (const botao of this.querySelectorAll('[data-claro-foto-abrir]')) {
      botao.addEventListener('click', () => this.#abre(), { signal });
    }
    this.querySelector('[data-claro-foto-fechar]')?.addEventListener('click', () => this.caixa?.close(), { signal });
    this.querySelector('[data-claro-foto-carregar]')?.addEventListener('click', () => this.ficheiro?.click(), { signal });
    this.ficheiro?.addEventListener('change', () => {
      const escolhido = this.ficheiro instanceof HTMLInputElement ? this.ficheiro.files?.[0] : null;
      if (escolhido) this.#carrega(escolhido);
    }, { signal });
    this.zoom?.addEventListener('input', () => this.#mudaZoom(Number(this.zoom?.value) || 1), { signal });
    this.querySelector('[data-claro-foto-menos]')?.addEventListener('click', () => this.#mudaZoom(this.#zoom / 1.2), { signal });
    this.querySelector('[data-claro-foto-mais]')?.addEventListener('click', () => this.#mudaZoom(this.#zoom * 1.2), { signal });

    // Uma foto arrastada do computador para a janela também serve
    this.caixa.addEventListener('dragover', (evento) => {
      evento.preventDefault();
      this.caixa?.setAttribute('data-a-largar', '');
    }, { signal });
    this.caixa.addEventListener('dragleave', (evento) => {
      if (evento.target === this.caixa) this.caixa?.removeAttribute('data-a-largar');
    }, { signal });
    this.caixa.addEventListener('drop', (evento) => {
      evento.preventDefault();
      this.caixa?.removeAttribute('data-a-largar');
      const largado = evento.dataTransfer?.files?.[0];
      if (largado?.type.startsWith('image/')) this.#carrega(largado);
    }, { signal });
    this.concluir?.addEventListener('click', () => this.#conclui(), { signal });

    // Carregar no fundo escuro, fora da janela, fecha
    this.caixa.addEventListener('click', (evento) => {
      if (evento.target === this.caixa) this.caixa?.close();
    }, { signal });

    this.tel.addEventListener('pointerdown', (evento) => this.#poisa(evento), { signal });
    this.tel.addEventListener('pointermove', (evento) => this.#mexe(evento), { signal });
    this.tel.addEventListener('pointerup', (evento) => this.#levanta(evento), { signal });
    this.tel.addEventListener('pointercancel', (evento) => this.#levanta(evento), { signal });
    // this.tel.addEventListener('wheel', (evento) => this.#roda(evento), { signal, passive: false });
    this.tel.addEventListener('keydown', (evento) => this.#tecla(evento), { signal });

    addEventListener('resize', () => this.#desenha(), { signal, passive: true });

    // Ao voltar atrás para esta página, o botão volta ao texto de sempre
    addEventListener('pageshow', () => {
      const texto = this.form?.querySelector('[data-claro-add-texto]');
      if (texto && this.textoBotao) texto.textContent = this.textoBotao;
      this.form?.querySelector('[data-claro-add]')?.removeAttribute('aria-busy');
    }, { signal });

    // Em captura: corre antes do envio da página de produto, e pára-o
    this.form?.addEventListener('submit', (evento) => this.#guarda(evento), { signal, capture: true });

    // A capa segue o iPhone e a cor escolhidos na página
    this.form?.addEventListener('change', () => this.#segueEscolha(), { signal });
    this.form?.addEventListener('click', (evento) => {
      if (evento.target instanceof Element && evento.target.closest('[data-claro-valor]')) this.#segueEscolha();
    }, { signal });
    this.#fonte()?.addEventListener('change', () => this.#segueEscolha(), { signal });
    this.#segueEscolha();
  }

  /** Abre a janela do editor. Para quem usa o editor fora da página de produto. */
  abre() {
    this.#abre();
  }

  /**
   * A lista de variantes de onde vem o iPhone, quando não há as opções da
   * página de produto (o kit NEXO × Vagar).
   * @returns {HTMLSelectElement | null}
   */
  #fonte() {
    const seletor = this.dataset.fonte;
    if (!seletor) return null;
    const raiz = this.dataset.fonteRaiz ? this.closest(this.dataset.fonteRaiz) : document;
    const lista = raiz?.querySelector(seletor);
    return lista instanceof HTMLSelectElement ? lista : null;
  }

  disconnectedCallback() {
    this.controlo?.abort();
    if (this.#urlMini) URL.revokeObjectURL(this.#urlMini);
  }

  /* ── abrir ── */
  #abre() {
    if (!(this.caixa instanceof HTMLDialogElement)) return;
    this.#segueEscolha();
    this.caixa.showModal();
    // A capa só tem medidas depois de a janela estar aberta
    this.#desenha();
  }

  /**
   * O valor escolhido na opção cujo nome casa com `nome` — venha de lista ou
   * de pastilhas. Sem essa opção, volta vazio.
   * @param {RegExp} nome
   */
  #valorDaOpcao(nome) {
    for (const grupo of document.querySelectorAll('[data-claro-opcao]')) {
      if (!nome.test(grupo.querySelector('.claro-opcao__nome')?.textContent ?? '')) continue;

      const lista = grupo.querySelector('select');
      return lista instanceof HTMLSelectElement
        ? lista.value
        : grupo.querySelector('[aria-pressed="true"]')?.getAttribute('data-claro-valor') ?? '';
    }
    // Sem as opções da página: o texto da variante escolhida ("Preto / Iphone 17 Pro")
    const fonte = this.#fonte();
    return fonte?.selectedOptions[0]?.textContent?.trim() ?? '';
  }

  #corDaCapa() {
    return /branc|white/i.test(this.#valorDaOpcao(/cor/i)) ? 'branco' : 'preto';
  }

  #modeloDaCapa() {
    return COM_BARRA.test(this.#valorDaOpcao(/iphone|modelo/i)) ? '17pro' : 'base';
  }

  /** A capa do editor segue o iPhone e a cor escolhidos na página */
  async #segueEscolha() {
    this.tel?.setAttribute('data-cor', this.#corDaCapa());

    const modelo = this.#modeloDaCapa();
    if (modelo === this.#modelo) return;
    this.#modelo = modelo;
    this.tel?.setAttribute('data-modelo', modelo);
    if (this.caixa instanceof HTMLDialogElement && this.caixa.open) this.#desenha();

    // Já concluída noutra capa: a imagem para impressão refaz-se na forma nova
    if (this.#feito) {
      try {
        await this.#exporta();
      } catch (erro) {
        console.error('claro-foto:', erro);
      }
    }
  }

  /* ── carregar ── */
  /** @param {File} escolhido */
  async #carrega(escolhido) {
    if (!this.img) return;

    const url = URL.createObjectURL(escolhido);
    const imagem = new Image();
    imagem.decoding = 'async';
    imagem.src = url;

    try {
      await imagem.decode();
    } catch {
      URL.revokeObjectURL(url);
      return;
    }

    if (this.img.src.startsWith('blob:')) URL.revokeObjectURL(this.img.src);

    this.#imagem = imagem;
    this.img.src = url;
    this.img.hidden = false;
    if (this.vazio) this.vazio.hidden = true;
    this.tel?.removeAttribute('data-vazio');

    this.#x = 0;
    this.#y = 0;
    this.#zoom = 1;
    if (this.zoom instanceof HTMLInputElement) {
      this.zoom.disabled = false;
      this.zoom.value = '1';
    }
    if (this.concluir instanceof HTMLButtonElement) this.concluir.disabled = false;
    for (const botao of this.querySelectorAll('[data-claro-foto-menos], [data-claro-foto-mais]')) {
      if (botao instanceof HTMLButtonElement) botao.disabled = false;
    }
    this.caixa?.setAttribute('data-etapa', '2');
    // Limpo, para escolher o mesmo ficheiro outra vez voltar a disparar
    if (this.ficheiro instanceof HTMLInputElement) this.ficheiro.value = '';

    this.#desenha();
    this.tel?.focus();
  }

  /* ── medidas ──────────────────────────────────────────────
     Tudo se guarda em frações da capa, e as medidas em píxeis saem daqui na
     hora. Assim, rodar o telemóvel ou mudar o tamanho da janela não tira a
     foto do sítio. */
  /** @param {{ width: number, height: number } | undefined} capa */
  #medidas(capa = this.ecra?.getBoundingClientRect()) {
    const imagem = this.#imagem;
    if (!imagem || !capa || !capa.width) return null;

    const cobre = Math.max(capa.width / imagem.naturalWidth, capa.height / imagem.naturalHeight);
    const escala = cobre * this.#zoom;
    return {
      cw: capa.width,
      ch: capa.height,
      escala,
      largura: imagem.naturalWidth * escala,
      altura: imagem.naturalHeight * escala,
    };
  }

  /**
   * Umas costas imaginárias com a largura dada e a proporção da capa em uso.
   * Como a posição se guarda em frações, servem para prender e para exportar
   * sem precisar da janela aberta.
   * @param {number} largura
   */
  #virtual(largura) {
    return { width: largura, height: largura * PROPORCAO[this.#modelo] };
  }

  /** Não deixa a foto sair da capa: o que sobra de cada lado é o limite */
  #prende() {
    const m = this.#medidas(this.#virtual(1000));
    if (!m) return;

    const folgaX = (m.largura - m.cw) / 2 / m.cw;
    const folgaY = (m.altura - m.ch) / 2 / m.ch;
    this.#x = Math.min(folgaX, Math.max(-folgaX, this.#x));
    this.#y = Math.min(folgaY, Math.max(-folgaY, this.#y));
  }

  #desenha() {
    this.#prende();
    const m = this.#medidas();
    if (!m || !this.img) return;

    const esquerda = m.cw / 2 + this.#x * m.cw - m.largura / 2;
    const topo = m.ch / 2 + this.#y * m.ch - m.altura / 2;

    this.img.style.width = `${m.largura}px`;
    this.img.style.height = `${m.altura}px`;
    this.img.style.transform = `translate(${esquerda}px, ${topo}px)`;

    // Quantos píxeis da foto cabem na largura da capa: é o que se imprime
    const nitidez = m.cw / m.escala;
    if (this.baixa) this.baixa.hidden = nitidez >= MINIMO_NITIDO;
  }

  /** Muda o zoom à volta do centro da capa: o que lá está fica lá */
  #mudaZoom(novo) {
    const zoom = Math.min(ZOOM_MAXIMO, Math.max(1, novo));
    const razao = zoom / this.#zoom;
    this.#x *= razao;
    this.#y *= razao;
    this.#zoom = zoom;
    if (this.zoom instanceof HTMLInputElement) this.zoom.value = String(zoom);
    this.#desenha();
  }

  #anda(dx, dy) {
    const m = this.#medidas();
    if (!m) return;
    this.#x += dx / m.cw;
    this.#y += dy / m.ch;
    this.#desenha();
  }

  /* ── ponteiros: um dedo arrasta, dois aproximam ── */
  /** @param {PointerEvent} evento */
  #poisa(evento) {
    if (!this.#imagem) return;
    this.tel?.setPointerCapture(evento.pointerId);
    this.#ponteiros.set(evento.pointerId, { x: evento.clientX, y: evento.clientY });
    this.#distancia = this.#afastamento();
    this.tel?.setAttribute('data-a-arrastar', '');
  }

  /** @param {PointerEvent} evento */
  #mexe(evento) {
    const antes = this.#ponteiros.get(evento.pointerId);
    if (!antes) return;

    const agora = { x: evento.clientX, y: evento.clientY };

    if (this.#ponteiros.size === 1) {
      this.#ponteiros.set(evento.pointerId, agora);
      this.#anda(agora.x - antes.x, agora.y - antes.y);
      return;
    }

    // Dois dedos: o zoom segue a distância entre eles, e o meio arrasta
    const meioAntes = this.#meio();
    this.#ponteiros.set(evento.pointerId, agora);
    const meioAgora = this.#meio();
    const distancia = this.#afastamento();

    if (this.#distancia > 0) this.#mudaZoom(this.#zoom * (distancia / this.#distancia));
    this.#distancia = distancia;
    this.#anda(meioAgora.x - meioAntes.x, meioAgora.y - meioAntes.y);
  }

  /** @param {PointerEvent} evento */
  #levanta(evento) {
    this.#ponteiros.delete(evento.pointerId);
    this.#distancia = this.#afastamento();
    if (!this.#ponteiros.size) this.tel?.removeAttribute('data-a-arrastar');
  }

  #afastamento() {
    if (this.#ponteiros.size < 2) return 0;
    const [a, b] = [...this.#ponteiros.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  #meio() {
    const pontos = [...this.#ponteiros.values()];
    return {
      x: pontos.reduce((soma, p) => soma + p.x, 0) / pontos.length,
      y: pontos.reduce((soma, p) => soma + p.y, 0) / pontos.length,
    };
  }

  /** @param {WheelEvent} evento */
  #roda(evento) {
    if (!this.#imagem) return;
    evento.preventDefault();
    this.#mudaZoom(this.#zoom * Math.exp(-evento.deltaY * 0.0015));
  }

  /** Setas mexem, + e − aproximam: quem não usa rato também enquadra */
  /** @param {KeyboardEvent} evento */
  #tecla(evento) {
    if (!this.#imagem) return;
    const passo = evento.shiftKey ? 40 : 10;
    const acoes = {
      ArrowLeft: () => this.#anda(-passo, 0),
      ArrowRight: () => this.#anda(passo, 0),
      ArrowUp: () => this.#anda(0, -passo),
      ArrowDown: () => this.#anda(0, passo),
      '+': () => this.#mudaZoom(this.#zoom * 1.1),
      '=': () => this.#mudaZoom(this.#zoom * 1.1),
      '-': () => this.#mudaZoom(this.#zoom / 1.1),
    };
    const acao = acoes[evento.key];
    if (!acao) return;
    evento.preventDefault();
    acao();
  }

  /** Conta a quem usa o editor que a foto está pronta, com os ficheiros */
  #avisa() {
    const ficheiro = (seletor) => {
      const campo = this.querySelector(seletor);
      return campo instanceof HTMLInputElement ? campo.files?.[0] ?? null : null;
    };
    const posicao = this.querySelector('[data-claro-foto-pos]');
    this.dispatchEvent(new CustomEvent('claro-foto:concluida', {
      bubbles: true,
      detail: {
        previa: ficheiro('[data-claro-foto-previa]'),
        imprimir: ficheiro('[data-claro-foto-final]'),
        original: ficheiro('[data-claro-foto-original]'),
        posicao: posicao instanceof HTMLInputElement ? posicao.value : '',
        miniatura: this.querySelector('[data-claro-foto-mini]')?.getAttribute('src') ?? '',
      },
    }));
  }

  /* ── concluir ── */
  async #conclui() {
    if (!this.#imagem) return;
    if (this.concluir instanceof HTMLButtonElement) this.concluir.disabled = true;

    try {
      await this.#exporta();
      this.#feito = true;
      this.caixa?.close();
      this.#avisa();
    } catch (erro) {
      console.error('claro-foto:', erro);
    } finally {
      if (this.concluir instanceof HTMLButtonElement) this.concluir.disabled = false;
    }
  }

  /**
   * As costas da capa desenhadas num canvas ao tamanho de impressão, a partir
   * da foto inteira — não do que está no ecrã, que é pequeno. As medidas
   * saem da proporção da capa em uso, por isso corre também com a janela
   * fechada.
   */
  async #exporta() {
    const imagem = this.#imagem;
    this.#prende();
    const m = this.#medidas(this.#virtual(LARGURA_FINAL));
    if (!imagem || !m) throw new Error('sem foto');

    const final = document.createElement('canvas');
    final.width = LARGURA_FINAL;
    final.height = Math.round(m.ch);

    const pincel = final.getContext('2d');
    if (!pincel) throw new Error('canvas');
    pincel.fillStyle = '#ffffff';
    pincel.fillRect(0, 0, final.width, final.height);
    pincel.imageSmoothingQuality = 'high';
    pincel.drawImage(
      imagem,
      m.cw / 2 + this.#x * m.cw - m.largura / 2,
      m.ch / 2 + this.#y * m.ch - m.altura / 2,
      m.largura,
      m.altura
    );

    const blobFinal = await this.#jpg(final, 0.95);

    // A foto na capa: se a moldura não se deixar desenhar, segue a das costas
    let blobPrevia = blobFinal;
    try {
      blobPrevia = await this.#previa(final);
    } catch (erro) {
      console.error('claro-foto: sem pré-visualização', erro);
    }

    // O original encolhe até o envio todo caber no limite
    let lado = LADO_ORIGINAL;
    let blobOriginal = await this.#original(imagem, lado);
    while (blobFinal.size + blobPrevia.size + blobOriginal.size > LIMITE_ENVIO && lado > 900) {
      lado = Math.round(lado * 0.8);
      blobOriginal = await this.#original(imagem, lado);
    }

    this.#poe('[data-claro-foto-final]', new File([blobFinal], 'capa-para-imprimir.jpg', { type: 'image/jpeg' }));
    this.#poe('[data-claro-foto-original]', new File([blobOriginal], 'foto-original.jpg', { type: 'image/jpeg' }));
    this.#poe('[data-claro-foto-previa]', new File([blobPrevia], 'foto-na-capa.jpg', { type: 'image/jpeg' }));

    const posicao = this.querySelector('[data-claro-foto-pos]');
    if (posicao instanceof HTMLInputElement) {
      const cor = this.tel?.getAttribute('data-cor') ?? '';
      posicao.value = `zoom ${this.#zoom.toFixed(2)} · x ${this.#x.toFixed(3)} · y ${this.#y.toFixed(3)} · capa ${this.#modelo} ${cor}`;
    }

    this.#mostraPronta(blobPrevia);
  }

  /**
   * @param {HTMLCanvasElement} tela
   * @param {number} qualidade
   */
  async #jpg(tela, qualidade) {
    const blob = await new Promise((resolve) => tela.toBlob(resolve, 'image/jpeg', qualidade));
    if (!(blob instanceof Blob)) throw new Error('toBlob');
    return blob;
  }

  /**
   * O original, em JPG, com o lado maior até `lado`
   * @param {HTMLImageElement} imagem
   * @param {number} lado
   */
  async #original(imagem, lado) {
    const reduz = Math.min(1, lado / Math.max(imagem.naturalWidth, imagem.naturalHeight));
    const tela = document.createElement('canvas');
    tela.width = Math.round(imagem.naturalWidth * reduz);
    tela.height = Math.round(imagem.naturalHeight * reduz);
    tela.getContext('2d')?.drawImage(imagem, 0, 0, tela.width, tela.height);
    return this.#jpg(tela, 0.92);
  }

  /**
   * A foto na capa, como se vê no editor: as costas já impressas, cortadas
   * pela forma da capa, e a moldura por cima. A moldura carrega-se de novo,
   * com permissão de origem, para o canvas a poder ler.
   * @param {HTMLCanvasElement} costas
   */
  async #previa(costas) {
    const chave = `${this.#modelo}-${this.tel?.getAttribute('data-cor') ?? 'preto'}`;
    const fonte = this.querySelector(`[data-moldura="${chave}"]`);
    if (!(fonte instanceof HTMLImageElement)) throw new Error('moldura');

    const moldura = new Image();
    moldura.crossOrigin = 'anonymous';
    moldura.src = fonte.currentSrc || fonte.src;
    await moldura.decode();

    const w = moldura.naturalWidth;
    const h = moldura.naturalHeight;
    const g = MOLDURA[this.#modelo];
    const ex = g.x * w, ey = g.y * h, ew = g.l * w, eh = g.a * h;
    const rx = g.rx * ew, ry = g.ry * eh;

    const tela = document.createElement('canvas');
    tela.width = w;
    tela.height = h;
    const pincel = tela.getContext('2d');
    if (!pincel) throw new Error('canvas');

    pincel.fillStyle = '#f2f0f6';
    pincel.fillRect(0, 0, w, h);

    // As costas com cantos elípticos, como o ecrã do editor
    pincel.save();
    pincel.beginPath();
    pincel.ellipse(ex + rx, ey + ry, rx, ry, 0, Math.PI, Math.PI * 1.5);
    pincel.ellipse(ex + ew - rx, ey + ry, rx, ry, 0, Math.PI * 1.5, Math.PI * 2);
    pincel.ellipse(ex + ew - rx, ey + eh - ry, rx, ry, 0, 0, Math.PI * 0.5);
    pincel.ellipse(ex + rx, ey + eh - ry, rx, ry, 0, Math.PI * 0.5, Math.PI);
    pincel.closePath();
    pincel.clip();
    pincel.drawImage(costas, ex, ey, ew, eh);
    pincel.restore();

    pincel.drawImage(moldura, 0, 0, w, h);
    return this.#jpg(tela, 0.9);
  }

  /**
   * @param {string} seletor
   * @param {File} ficheiro
   */
  #poe(seletor, ficheiro) {
    const campo = this.querySelector(seletor);
    if (!(campo instanceof HTMLInputElement)) return;
    const transporte = new DataTransfer();
    transporte.items.add(ficheiro);
    campo.files = transporte.files;
  }

  /** @param {Blob} blob */
  #mostraPronta(blob) {
    const mini = this.querySelector('[data-claro-foto-mini]');
    if (mini instanceof HTMLImageElement) {
      if (this.#urlMini) URL.revokeObjectURL(this.#urlMini);
      this.#urlMini = URL.createObjectURL(blob);
      mini.src = this.#urlMini;
    }

    const escolher = this.querySelector('.claro-foto__escolher');
    const pronta = this.querySelector('[data-claro-foto-pronta]');
    if (escolher instanceof HTMLElement) escolher.hidden = true;
    if (pronta instanceof HTMLElement) pronta.hidden = false;
    if (this.erro) this.erro.hidden = true;
  }

  /* ── o formulário ── */
  /** @param {SubmitEvent} evento */
  #guarda(evento) {
    const final = this.querySelector('[data-claro-foto-final]');
    if (final instanceof HTMLInputElement && final.files?.length) {
      evento.preventDefault();
      evento.stopImmediatePropagation();
      this.#envia();
      return;
    }

    evento.preventDefault();
    evento.stopImmediatePropagation();

    if (this.erro) this.erro.hidden = false;
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const escolher = this.querySelector('.claro-foto__escolher');
    if (escolher instanceof HTMLElement) escolher.focus({ preventScroll: true });
  }

  /**
   * O envio para o carrinho, feito aqui e não pelo formulário.
   *
   * A Shopify guarda as fotos e devolve um link para cada uma — mas, aberto
   * num navegador, esse link chega em WebP, e quem faz a capa precisa de JPG.
   * Com `format=pjpg` o mesmo link entrega o ficheiro original. Por isso,
   * logo a seguir a juntar a capa, os links da linha são trocados por esses.
   *
   * Os extras (pack, artigo a juntar) vão a seguir. Se o primeiro passo
   * falhar, segue o formulário normal: a capa chega na mesma, só sem o JPG
   * forçado — antes isso do que uma venda perdida.
   */
  async #envia() {
    const form = this.form;
    if (!(form instanceof HTMLFormElement)) return;

    const texto = form.querySelector('[data-claro-add-texto]');
    const botao = form.querySelector('[data-claro-add]');
    if (texto) {
      this.textoBotao ??= texto.textContent;
      texto.textContent = 'A enviar a tua foto…';
    }
    botao?.setAttribute('aria-busy', 'true');

    const rotas = window.Theme?.routes ?? {};
    const juntar = rotas.cart_add_url || '/cart/add';
    const mudar = rotas.cart_change_url || '/cart/change';
    const carrinho = rotas.cart_url || '/cart';

    let linha;
    try {
      const resposta = await fetch(juntar, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      linha = await resposta.json();
      if (!resposta.ok || linha.status) throw new Error(linha.description || linha.message || String(resposta.status));
    } catch (erro) {
      console.error('claro-foto:', erro);
      form.submit();
      return;
    }

    try {
      const propriedades = Object.fromEntries(
        Object.entries(linha.properties ?? {}).map(([nome, valor]) => [nome, comoJpg(valor)])
      );
      await fetch(mudar, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: linha.key, quantity: linha.quantity, properties: propriedades }),
      });

      const produto = this.closest('claro-produto');
      const extras = produto && 'extrasEscolhidos' in produto ? produto.extrasEscolhidos() : [];
      if (extras.length) {
        await fetch(juntar, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: extras.map((id) => ({ id, quantity: 1 })) }),
        });
      }
    } catch (erro) {
      // A capa já está no carrinho: o resto não justifica parar
      console.error('claro-foto:', erro);
    }

    location.href = carrinho;
  }
}

if (!customElements.get('claro-foto')) {
  customElements.define('claro-foto', ClaroFoto);
}
